import { BoardWithColumns } from "frontend/canvas-designer-new/monday-import-items/monday-import.hook";
import { MondayColumn } from "shared/integrations/mapper";

export const ColumnForName = /(Employee )?Name/i;
export const ColumnForDirectManager = /direct manager/i;
export const ColumnForPosition = /position|role|job/i;

const requiredColumnTypes = {
  "Employee name": ["text", "email", "people", "name"],
  "Direct manager": ["text", "email", "people"],
  Position: ["text"],
} as const;

const requiredColumns = Object.values(requiredColumnTypes);

export function findAutoMappedColumnId(
  columns: MondayColumn[],
  supportedTypes: readonly string[],
  columnRegex: RegExp
) {
  const mappedColumn = columns.find((col) => supportedTypes.includes(col.type) && columnRegex.test(col.title));
  return mappedColumn?.id ?? null;
}

export function getFilteredAndSortedColumns(
  columns: MondayColumn[],
  validTypes: readonly string[],
  selectedId: string | null
): MondayColumn[] {
  const enabledColumns = columns.filter((col) => validTypes.includes(col.type));
  const disabledColumns = columns.filter((col) => !validTypes.includes(col.type));

  const sortWithSelected = (cols: MondayColumn[]) =>
    cols.sort((a, b) => {
      if (a.id === selectedId) return -1;
      if (b.id === selectedId) return 1;
      return a.title.localeCompare(b.title);
    });

  // Return enabled columns first, then disabled ones
  return [...sortWithSelected(enabledColumns), ...sortWithSelected(disabledColumns)];
}

/**
 * Check if a board has the minimum required columns for an org chart.
 * Exported so it can be used as a filter function in useMondayImportItems.
 */
export function orgChartBoardFilter(board: BoardWithColumns): boolean {
  const columns = board.columns ?? [];

  if (columns.length < requiredColumns.length) {
    return false;
  }

  function findMatch(cols: { type?: string }[], requiredIndex = 0): boolean {
    if (requiredIndex >= requiredColumns.length) {
      return true;
    }
    if (requiredColumns.length - requiredIndex > cols.length) {
      return false;
    }
    for (let typeIndex = 0; typeIndex < requiredColumns[requiredIndex].length; typeIndex++) {
      const columnIndex = cols.findIndex((col) => col.type === requiredColumns[requiredIndex][typeIndex]);
      if (columnIndex !== -1) {
        const remainingColumns = cols.filter((_, index) => index !== columnIndex);
        if (findMatch(remainingColumns, requiredIndex + 1)) {
          return true;
        }
      }
    }
    return false;
  }

  return findMatch(columns);
}

export function getColumnText(
  columnValues: Array<{ id: string; type: string; text?: string; value?: string }> | undefined,
  columnId: string | null
): string {
  if (!columnValues || !columnId) return "";
  const column = columnValues.find((col) => col.id === columnId);
  return column?.text || "";
}
